import React from 'react';
import type { GlossaryTerm } from './admin-ia-shared';
import { INPUT_STYLE, LABEL_STYLE, SECTION_CARD, SECTION_TITLE } from './admin-ia-shared';

interface AdminIAAdvancedProps {
  comportamiento: string;
  contexto: string;
  glossary: GlossaryTerm[];
  configData: Record<string, any> | null;
  saving: boolean;
  onSetComportamiento: (v: string) => void;
  onSetContexto: (v: string) => void;
  onSetGlossary: React.Dispatch<React.SetStateAction<GlossaryTerm[]>>;
  onUpdateGlossaryTerm: (idx: number, patch: Partial<GlossaryTerm>) => void;
  onRemoveGlossaryTerm: (idx: number) => void;
  onSaveConfig: (patch: Record<string, any>, feedback?: string) => Promise<void>;
  onSetConfigData: React.Dispatch<React.SetStateAction<Record<string, any> | null>>;
}

export function AdminIAAdvanced({
  comportamiento,
  contexto,
  glossary,
  configData,
  saving,
  onSetComportamiento,
  onSetContexto,
  onSetGlossary,
  onUpdateGlossaryTerm,
  onRemoveGlossaryTerm,
  onSaveConfig,
  onSetConfigData,
}: AdminIAAdvancedProps) {
  return (
    <div style={SECTION_CARD}>
      <h3 style={SECTION_TITLE}>Instrucciones y glosario</h3>

      {/* Comportamiento */}
      <div style={{ marginBottom: 14 }}>
        <label style={LABEL_STYLE}>Comportamiento (system prompt)</label>
        <textarea
          rows={3}
          style={{ ...INPUT_STYLE, resize: 'vertical' }}
          value={comportamiento}
          onChange={(e) => onSetComportamiento(e.target.value)}
        />
      </div>

      {/* Contexto sectorial */}
      <div style={{ marginBottom: 14 }}>
        <label style={LABEL_STYLE}>Contexto sectorial</label>
        <textarea
          rows={2}
          style={{ ...INPUT_STYLE, resize: 'vertical' }}
          value={contexto}
          onChange={(e) => onSetContexto(e.target.value)}
        />
      </div>

      {/* Glosario */}
      <div style={{ marginBottom: 14 }}>
        <label style={LABEL_STYLE}>Glosario</label>
        <div style={{ background: '#f3f4f6', borderRadius: 6, padding: 10 }}>
          {glossary.map((g, idx) => (
            <div
              key={idx}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '5px 0',
                borderBottom: idx < glossary.length - 1 ? '1px solid #e5e7eb' : undefined,
              }}
            >
              <input
                type="text"
                style={{
                  ...INPUT_STYLE,
                  width: 120,
                  fontSize: 12,
                  padding: '4px 8px',
                  fontWeight: 600,
                }}
                value={g.term}
                onChange={(e) => onUpdateGlossaryTerm(idx, { term: e.target.value })}
              />
              <input
                type="text"
                style={{ ...INPUT_STYLE, flex: 1, fontSize: 11, padding: '4px 8px' }}
                value={g.definition}
                onChange={(e) => onUpdateGlossaryTerm(idx, { definition: e.target.value })}
              />
              <button
                onClick={() => onRemoveGlossaryTerm(idx)}
                style={{
                  padding: '2px 8px',
                  background: '#FEE2E2',
                  color: '#DC2626',
                  border: 'none',
                  borderRadius: 4,
                  fontSize: 10,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontWeight: 600,
                }}
              >
                &times;
              </button>
            </div>
          ))}
          <button
            onClick={() => onSetGlossary([...glossary, { term: '', definition: '' }])}
            style={{
              marginTop: 8,
              padding: '4px 12px',
              background: 'transparent',
              border: '1px dashed #d1d5db',
              borderRadius: 4,
              fontSize: 10,
              color: '#9ca3af',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            + Anadir termino
          </button>
        </div>
      </div>

      {/* Modo avanzado */}
      <div style={SECTION_CARD}>
        <h3 style={SECTION_TITLE}>Modo avanzado</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {(
            [
              {
                key: 'agentMode',
                label: 'Modo Agente',
                desc: 'RAG agentico con planificacion de herramientas',
              },
              {
                key: 'reflectionEnabled',
                label: 'Self Reflection',
                desc: 'El agente evalua y mejora su propia respuesta',
              },
              {
                key: 'sentinelEnabled',
                label: 'Sentinel activo',
                desc: 'Vigilancia proactiva de gobernanza',
              },
              {
                key: 'ragMultiQuery',
                label: 'Multi Query',
                desc: 'Expande la query en multiples sub-queries',
              },
              {
                key: 'ragHyde',
                label: 'HyDE',
                desc: 'Hypothetical Document Embeddings para mejor retrieval',
              },
              {
                key: 'vectorlessEnabled',
                label: 'Vectorless RAG',
                desc: 'Navegacion estructural de documentos ProseMirror',
              },
              {
                key: 'autoRepairEnabled',
                label: 'Auto-repair scanning',
                desc: 'Deteccion automatica de inconsistencias documentales',
              },
            ] as const
          ).map((toggle) => (
            <label
              key={toggle.key}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 12px',
                borderRadius: 8,
                border: '1px solid #e5e7eb',
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={!!configData?.[toggle.key]}
                onChange={() =>
                  onSaveConfig(
                    { [toggle.key]: !configData?.[toggle.key] },
                    `${toggle.label} ${!configData?.[toggle.key] ? 'activado' : 'desactivado'} \u2713`,
                  ).then(() =>
                    onSetConfigData((prev) =>
                      prev ? { ...prev, [toggle.key]: !prev[toggle.key] } : prev,
                    ),
                  )
                }
                style={{ width: 16, height: 16, accentColor: '#1F3864' }}
              />
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
                  {toggle.label}
                </div>
                <div style={{ fontSize: 11, color: '#6b7280' }}>{toggle.desc}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Guardar todo */}
      <button
        onClick={() =>
          onSaveConfig(
            { comportamiento, contextoSectorial: contexto, glosario: glossary },
            'Configuracion guardada \u2713',
          )
        }
        disabled={saving}
        style={{
          padding: '8px 20px',
          background: saving ? '#6b7280' : '#0E243E',
          color: '#fff',
          border: 'none',
          borderRadius: 6,
          fontSize: 12,
          fontWeight: 600,
          cursor: saving ? 'default' : 'pointer',
          fontFamily: 'inherit',
          opacity: saving ? 0.7 : 1,
        }}
      >
        {saving ? 'Guardando...' : 'Guardar todo'}
      </button>
    </div>
  );
}
